import VoltError from 'VoltError'

/**
 * Mapping Error Table from the Backend
 * Documentation: https://docs.nagra.com/doc/otv/otvdoc2051/integration-guides/client-integration-guide/authentication/sign-in-error-code-management
 * Documentation: https://docs.google.com/spreadsheets/d/1TN9hGELG0IJ3N-cSsw2dOu_zC0OY8gfR/edit#gid=1986377033
 */
export const ERROR_BACKEND_MAPPING = {
    // Account cancelled by operator, this situation can be recovered.
    // Provision a device to an Account that's already cancelled
    20001: () => VoltError.codes.USER_ACCOUNT_TERMINATED,
    // Request a token signon to an Account that's already suspended
    20002: () => VoltError.codes.USER_IS_BARRED,
    // Account suspended by operator, this situation can be recovered.
    // Sign on to an Account with a unknown device id that is not registered in the platform.
    20003: () => VoltError.codes.AUTH_UNKNOWN_DEVICE,
    // An on screen error is not fatal, if the user does an uninstall or removes the app the situation can be recovered.
    // Sign on to an Account with a device id that is in "DISABLED" status on the platform.
    20004: () => VoltError.codes.AUTH_DISABLED_DEVICE,
    // Sign on to an Account with a device id that is in "INACTIVE" activation status on the platform
    20005: () => VoltError.codes.AUTH_INACTIVE_DEVICE,
    // Device invalid account id
    20006: () => VoltError.codes.INVALID_ACCOUNT_ID,
    // Sign on to an Account with 6th Device which has account profile "ResidentialD5S2", allowing only 5 devices
    20007: () => VoltError.codes.MAX_DEVICES_REACHED,
    // Invalid Device data in the Device blob provided as part of Device body.
    20011: () => VoltError.codes.AUTH_ERROR_METADATAS_DEVICE,
    // User attempts to log on or playback content and the jti check fails
    20020: () => VoltError.codes.INVALID_JTI_TOKEN,
    400: (errorCode, description) => {
        /**
         * Specification is available here:
         * https://docs.nagra.com/doc/home/otvdoc2051/apis/identity-authentication-service-ias-api-documentation#/V1%20Starhub%20OAUTH%20STB%20Signon/signonByOAuthTokenAndCASN_1
         */
        switch (errorCode) {
            case 4306: // 4306 - Invalid request. - ADM reports DEVICE_NOT_MANAGED
                return VoltError.codes.AUTH_UNKNOWN_DEVICE
            case 4305: // 4305 - Invalid request. - ADM reports DEVICE_NOT_ACTIVE
                return VoltError.codes.AUTH_INACTIVE_DEVICE
            case 4304: // 4304 - Invalid request. - ADM reports DEVICE_NOT_ENABLED
                return VoltError.codes.AUTH_DISABLED_DEVICE
            case 4303: // 4303 - Invalid request. - ADM reports DEVICE_NOT_KNOWN
                return VoltError.codes.AUTH_DEVICE_NOT_REGISTERED
            case 4302: // 4302 - Invalid request. - ADM reports ACCOUNT_SUSPENDED
                return VoltError.codes.USER_IS_BARRED
            case 4301: // 4301 - Invalid request. - ADM reports ACCOUNT_CANCELLED
                return VoltError.codes.USER_ACCOUNT_TERMINATED
            case 4205: // 4205 - Invalid request. - ADM reports unexpected exception - see logs
                return VoltError.codes.AUTH_MISSING_PARAMETERS
            case 4204: // 4204 - Invalid request. - ADM reports internal error - see logs
                return VoltError.codes.AUTH_ERROR
            case 4203: // 4203 - Invalid request. - ADM reports account not found
                return VoltError.codes.ACCOUNT_NOT_FOUND
            case 4202: // 4202 - Invalid request. - ADM reports tenant not found
                return VoltError.codes.MISSING_TENANT_ID
            case 4201: // 4201 - Invalid request. - ADM reports invalid tenant id
                return VoltError.codes.MISSING_TENANT_ID
            case 4200: // 4200 - Invalid request. - ADM reports invalid request response - see logs
                return VoltError.codes.AUTH_DEVICE_NOT_REGISTERED
            case 4005: // 4005 - Invalid request. - Starhub external ids do not match for sso_id and casn accounts.
                return VoltError.codes.AUTH_MISSING_PARAMETERS
            case 4004: // 4004 - Invalid request. - Starhub token validation api request received unexpected exception - see logs
                return VoltError.codes.AUTH_MISSING_PARAMETERS
            case 4003: // 4003 - Invalid request. - Starhub token is invalid.
                return VoltError.codes.INVALID_AUTH_TOKEN
            case 4002: // 4002 - Invalid request. - Starhub CASN parameter is missing.
                return VoltError.codes.AUTH_ERROR_METADATAS_DEVICE
            case 4001: // 4001 - Invalid request. - StarHub OAuth token parameter is missing.
                return VoltError.codes.INVALID_AUTH_TOKEN
            default:
                return VoltError.codes.AUTH_ERROR
        }
    },
    // Invalid Token (NOT TESTED)
    401: () => VoltError.codes.INVALID_AUTH_TOKEN,
    500: (errorCode, description) => {
        switch (errorCode) {
            case 6001: // Returned for nown when PIN cannot be retrieved due to internal error
                return VoltError.codes.SERVICE_NOT_ALLOWED
            default:
                return VoltError.codes.SERVICE_NOT_ALLOWED
        }
    },
    // Unauthorized
    Unauthorized: () => VoltError.codes.AUTH_UNAUTHORIZED_USER,
    // Bearer Token Signature Invalid or Token Expired
    '99999:403': () => VoltError.codes.AUTH_OAUTH_EXPIRED_TOKEN,
    // JSON Parse Error
    '200-001': () => VoltError.codes.INVALID_AUTH_TOKEN,
    // URL Error
    '200-002': () => VoltError.codes.URL_ERROR,
    // Unknown Error
    '200-003': () => VoltError.codes.UNKNOWN_BACKEND_ERROR,
    /**
     * Message: Call to ADM resulted in error. Status: %s, message %s
     * Description: General error from ADM during authentication.
     */
    1000: () => VoltError.codes.RECORDING_NO_RIGHT_FOR_ACCOUNT,
    /**
     * Message: No NPVR profile found for account: %s
     * Description: The ADM account does not have a valid npvrProfileName configured.
     */
    1001: () => VoltError.codes.RECORDING_NO_RIGHT_FOR_ACCOUNT,
    /**
     * Message: ADM Account %s has npvrProfileName %s which is not recognised in CDVR QuotaProfileService
     * Description: The profile configured in the ADM account doesn't match any that are configured in CDVR
     */
    1002: () => VoltError.codes.RECORDING_NO_RIGHT_FOR_ACCOUNT,
    /**
     * Message: Recording event: %s would exceed quota. Account: %s, Remaining Quota: %s, Requested: %s
     * Description: The recording request would exceed the account's quota.
     */
    1003: () => VoltError.codes.EXCEEDED_QUOTA,
    /**
     * Message: QuotaProfile: Retention period %s format should be in standard ISO-8601 duration format[ex- P3Y6M4DT12H30M5S]
     * Description: Invalid retention period format
     */
    1004: () => VoltError.codes.RETENTION_PERIOD,
    /**
     * Message: Recording event: Failed to record event %s for account %s due to quota not available
     * Description: The recording request failure due to exceed the account's quota.
     */
    1005: () => VoltError.codes.EXCEEDED_QUOTA,
    /**
     * Message: To enable AutoDelete, at least one of [%s] or [%s] should be enabled
     * Description: Invalid configuratoin of auto deletion field
     */
    1006: () => VoltError.codes.AUTO_DELETE,
    /**
     * Message: QuotaProfile: Profile [%s] already exists or remove space in the profile name
     * Description: Invalid quota profile
     */
    1007: () => VoltError.codes.INVALID_QUOTA,
    /**
     * Message: Account is not authorised
     * Description: The ADM account is not active.
     */
    1050: () => VoltError.codes.RECORDING_NO_QUOTA_SUBSCRIPTION,
    /**
     * Message: Unable to authorise
     * Description: Unable to contact the ADM service.
     */
    1051: () => VoltError.codes.RECORDING_NO_RIGHT_FOR_ACCOUNT,
    /**
     * Message: Message received from ADM queue was null
     * Description: Invalid message from ADM
     */
    1052: () => VoltError.codes.URL_ERROR,
    /**
     * Message: Not entitled to content or associated product, or content not found	General issue attempting to check entitlements with RMG.
     * Description:
     */
    1060: () => VoltError.codes.RECORDING_NOT_FOUND,
    /**
     * Message: Message received from RMG queue was null
     * Description: Invalid message from RMG
     */
    1061: () => VoltError.codes.URL_ERROR,
    /**
     * Message: RMG Entitlement notification message has no accountId
     * Description: Invalid entitlment notification from RMG
     */
    1062: () => VoltError.codes.INVALID_ACCOUNT_ID,
    /**
     * Message: RMG Entitlement notification message has no productId
     * Description: Invalid entitlment notification from RMG
     */
    1063: () => VoltError.codes.MISSING_PRODUCT,
    /**
     * Message: Days to advance must be greater than zero
     * Description: Invalid purge early request parameter.
     */
    1100: () => VoltError.codes.RECORDING_REQUEST_ERROR,
    /**
     * Message: The account threshold must not be negative
     * Description: Invalid purge early request parameter.
     */
    1101: () => VoltError.codes.RECORDING_REQUEST_ERROR,
    /**
     * Message: The minimum age in days must not be negative
     * Description: Invalid purge early request parameter.
     */
    1102: () => VoltError.codes.RECORDING_REQUEST_ERROR,
}
